import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { ButtonBase } from '@material-ui/core';
import ChatBubble from './ChatBubble';
import ChatMark from './ChatMark';
import tokens from '../theme/tokens';

// 대화 목록 — 날짜 칩으로 묶고, 새 메시지가 오면 아래로 따라간다. 위에는 [이전 대화 보기]
const useStyles = makeStyles(() => ({
  root: {
    flex: '1 1 auto',
    minHeight: 0,
    overflowY: 'auto',
    padding: '16px 16px 8px',
    backgroundColor: '#fffaf8',
    WebkitOverflowScrolling: 'touch',
  },
  more: {
    display: 'block',
    margin: '0 auto 14px',
    height: 30,
    padding: '0 14px',
    borderRadius: 15,
    border: `1px solid ${tokens.line}`,
    backgroundColor: tokens.surface,
    color: tokens.text,
    fontSize: 12,
    fontWeight: 700,
    '&:hover': { borderColor: tokens.primary, color: tokens.primaryDark },
  },
  date: {
    margin: '4px auto 14px',
    width: 'fit-content',
    padding: '3px 10px',
    borderRadius: 10,
    backgroundColor: tokens.surface,
    border: `1px solid ${tokens.lineSoft}`,
    color: tokens.muted,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: -0.4,
  },
  intro: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '36px 12px 28px',
    textAlign: 'center',
  },
  introTitle: {
    marginTop: 14,
    fontSize: 17,
    fontWeight: 700,
    letterSpacing: -0.8,
    color: tokens.ink,
  },
  introLine: {
    marginTop: 6,
    fontSize: 13,
    lineHeight: 1.6,
    letterSpacing: -0.4,
    color: tokens.text,
    wordBreak: 'keep-all',
  },
}));

const dayKey = (at) => {
  const d = new Date(at);
  return isNaN(d.getTime()) ? '' : `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
};
const dayLabel = (at) => {
  const d = new Date(at);
  const today = new Date();
  const yesterday = new Date(today.getTime() - 86400000);
  if (dayKey(d) === dayKey(today)) return '오늘';
  if (dayKey(d) === dayKey(yesterday)) return '어제';
  return `${d.getMonth() + 1}월 ${d.getDate()}일`;
};

function ChatThread({ messages, hasMore, loading, onLoadMore, intro }) {
  const cs = useStyles();
  const box = useRef(null);
  const stick = useRef(true);           // 바닥 근처를 보고 있으면 새 메시지 때 따라 내려간다
  const prevFirst = useRef('');

  const onScroll = () => {
    const el = box.current;
    if (!el) return;
    stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };

  // 새 메시지 → 바닥으로. 이전 대화를 위에 끼워 넣었을 때는 보던 자리를 유지한다
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    const first = messages.length ? messages[0].id : '';
    if (prevFirst.current && first && first !== prevFirst.current && messages.some((m) => m.id === prevFirst.current)) {
      const anchor = el.querySelector(`[data-id="${prevFirst.current}"]`);
      if (anchor) el.scrollTop = anchor.offsetTop - 16;
    } else if (stick.current) {
      el.scrollTop = el.scrollHeight;
    }
    prevFirst.current = first;
  }, [messages]);

  useEffect(() => {
    const el = box.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, []);

  let lastDay = '';
  return (
    <div className={cs.root} ref={box} onScroll={onScroll}>
      {hasMore && (
        <ButtonBase className={cs.more} onClick={onLoadMore} disabled={loading} focusRipple={false}>
          {loading ? '불러오는 중' : '이전 대화 보기'}
        </ButtonBase>
      )}
      {!messages.length && (
        <div className={cs.intro}>
          <ChatMark size={72} ring />
          <div className={cs.introTitle}>퀴노스 실시간문의</div>
          {intro && <div className={cs.introLine}>{intro}</div>}
        </div>
      )}
      {messages.map((m, idx) => {
        const key = dayKey(m.at);
        const showDate = key && key !== lastDay;
        lastDay = key || lastDay;
        const prev = messages[idx - 1];
        const sameSender = prev && prev.senderKind === m.senderKind && prev.senderName === m.senderName && prev.senderKind !== 'system';
        return (
          <div key={m.id} data-id={m.id}>
            {showDate && <div className={cs.date}>{dayLabel(m.at)}</div>}
            <ChatBubble message={m} showName={!sameSender} />
          </div>
        );
      })}
    </div>
  );
}

export default ChatThread;
